/**
 * Password reset confirmation page (token-based).
 */
import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

export default function ResetPassword() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const token = search.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    if (!token) return toast.error("Missing reset token");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="reset-pw-done" className="max-w-md mx-auto px-6 py-20 text-center text-white">
        <CheckCircle2 size={48} className="mx-auto text-emerald-400" />
        <h1 className="font-serif-display text-3xl mt-5">Password reset</h1>
        <p className="text-neutral-300 mt-3 text-sm">Redirecting you to sign in…</p>
      </div>
    );
  }

  return (
    <div data-testid="reset-pw" className="max-w-md mx-auto px-6 py-16 text-white">
      <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Account</div>
      <h1 className="font-serif-display text-3xl mt-2">Set a new <span className="italic text-[#D4AF37]">password</span></h1>
      <p className="text-neutral-300 mt-3 text-sm">Choose something strong — at least 8 characters.</p>

      <div className="mt-8 space-y-4">
        <div>
          <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">New password</div>
          <input data-testid="reset-new" className={inp} type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
        </div>
        <div>
          <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">Confirm</div>
          <input data-testid="reset-confirm" className={inp} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <button data-testid="reset-submit" disabled={busy} onClick={submit} className="btn-gold w-full px-6 py-3 rounded text-xs tracking-widest uppercase font-semibold inline-flex justify-center items-center gap-2">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Set new password
        </button>
      </div>
    </div>
  );
}
